import type { FC, VFC } from 'react'

import CdnImage from 'views/components/atoms/CdnImage'
import Image from 'views/components/atoms/Image'
import {
  IMAGE_MAX_HEIGHT,
  IMAGE_MAX_WIDTH,
  IMAGE_MIN_WIDTH,
  IMAGE_SIZES,
} from 'views/components/organisms/FilteredProductList/consts'

import { CLASS_SELECTABLE } from './consts'

export type SearchProductListItemComponentProps = {
  searchProductListItemProps: Record<string, unknown>
  className: string
  highlighted: boolean
  highlightedProductLabel: VFC
  searchProductLabelsComponent: VFC
  currentOfferId: string
  redpointsMultiplier: number
  handleProductCardClick: (
    event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLDivElement>
  ) => void
  productUrl: string
  productUrlWithImageEvent: string
  lazyLoadImage: boolean
  isCdnUrl: boolean
  imageProps: {
    prescriptionMedicine: boolean
    discount: number
    imageUrl: string
    multiplierRP: number
    name: string
    prescriptionType: string
    prices: []
    productTemplate: string
    redPoints: number
    redPointsIsActive: boolean
    redPointsMultiplierIsValid: boolean
    srcHeight: number
    srcWidth: number
    thumbnail: boolean
  }
  pzn: string
  titleClassName: string
  shouldDisplayRatings: boolean
  starRatingComponent: VFC
  idSwitcher: VFC
  isItemWithMultipleVariantAttributes: boolean
  quantityUnit: string
  pharmaForm: string
  manufacturer: string
  productStatus: VFC
  isRebrandingEnabled: boolean
  renderProductItemLinkWithIcon: VFC
  textInfoClassName: string
  descriptionShort: string
  odsPartnersComponent: VFC
  isRetailPricePresent: boolean
  discountBadge: VFC
  retailPriceComponent: VFC
  unitPricingComponent: VFC
  listPriceWithDiscount: VFC
  addToCartButtonComponent: VFC
  sellerBoxComponent: VFC
  renderProductVariantsBox: FC
}

const SearchProductListItemComponent: VFC<
  SearchProductListItemComponentProps
> = ({
  searchProductListItemProps,
  className,
  highlighted,
  highlightedProductLabel,
  searchProductLabelsComponent,
  currentOfferId,
  handleProductCardClick,
  productUrl,
  productUrlWithImageEvent,
  imageProps,
  isCdnUrl,
  lazyLoadImage,
  titleClassName,
  starRatingComponent,
  isItemWithMultipleVariantAttributes,
  quantityUnit,
  pharmaForm,
  idSwitcher,
  manufacturer,
  productStatus,
  isRebrandingEnabled,
  renderProductItemLinkWithIcon,
  textInfoClassName,
  descriptionShort,
  odsPartnersComponent,
  discountBadge,
  retailPriceComponent,
  unitPricingComponent,
  listPriceWithDiscount,
  addToCartButtonComponent,
  sellerBoxComponent,
  renderProductVariantsBox,
}) => {
  const { imageUrl, name } = imageProps

  return (
    <li {...searchProductListItemProps} className={className}>
      {highlighted && (
        <div className="o-SearchProductListItem__highlighted-area u-border u-border-radius--top-left u-border-radius--top-right a-h3">
          {highlightedProductLabel}
        </div>
      )}
      {searchProductLabelsComponent}
      <div
        className="o-SearchProductListItem__link u-color--dark u-padding--sides u-padding--bottom l-flex l-flex--wrap l-flex--stretch-children"
        data-qa-id={Boolean(currentOfferId) && `offers-link-${currentOfferId}`}
        data-clientside-hook="productDetailButton"
        role="button"
        tabIndex={0}
        onClick={event => {
          handleProductCardClick(event)
        }}
        onKeyDown={handleProductCardClick}
      >
        <a
          className="o-SearchProductListItem__image"
          href={productUrlWithImageEvent}
          draggable={false}
        >
          {isCdnUrl ? (
            <CdnImage
              {...{
                fileName: imageUrl,
                alt: name,
                className: 'a-fullwidth-image',
                sizes: IMAGE_SIZES,
                minWidth: IMAGE_MIN_WIDTH,
                maxWidth: IMAGE_MAX_WIDTH,
                maxHeight: IMAGE_MAX_HEIGHT,
                lazyLoad: lazyLoadImage,
                preconnect: !lazyLoadImage,
              }}
            />
          ) : (
            <Image
              {...{
                src: imageUrl,
                alt: name,
                className: 'a-fullwidth-image',
                lazyLoad: lazyLoadImage,
                preconnect: !lazyLoadImage,
              }}
            />
          )}
        </a>

        <div className="o-SearchProductListItem__content l-flex l-flex--stretch-children l-flex__primary l-flex--wrap@small-tiny">
          <div className="l-col--12-of-12 l-col--7-of-12@medium u-padding--sides o-SearchProductListItem__content__header">
            <div className="u-margin--bottom o-SearchProductListItem__content__header__top">
              <a className={titleClassName} href={productUrl} draggable={false}>
                {name}
              </a>
              {starRatingComponent}
            </div>
            <div className={CLASS_SELECTABLE}>
              <ul className="o-SearchProductListItem__info-list u-margin--bottom u-margin-small--bottom@small-tiny">
                {!isItemWithMultipleVariantAttributes && (
                  <li className="o-SearchProductListItem__info-list__item">
                    <span
                      className="a-h4-tiny u-font-weight--bold o-SearchProductListItem__info__items"
                      suppressHydrationWarning
                    >
                      {quantityUnit}
                      {pharmaForm ? ` | ${pharmaForm}` : ''}
                    </span>
                  </li>
                )}
                {idSwitcher}
                {manufacturer && (
                  <li className="u-color--grey o-SearchProductListItem__info-list__item">
                    <span className="a-h4-tiny o-SearchProductListItem__info__items">
                      {manufacturer}
                    </span>
                  </li>
                )}
              </ul>
              {productStatus}
            </div>
          </div>
          {isRebrandingEnabled && renderProductItemLinkWithIcon}
          <div className={textInfoClassName}>
            {descriptionShort && (
              <a
                href={productUrl}
                draggable={false}
                className="a-link--neutral u-hidden@small-tiny"
              >
                <p className="a-h4-tiny o-SearchProductListItem__content__body__text">
                  {descriptionShort}
                </p>
              </a>
            )}
            {odsPartnersComponent}
            <div className="l-flex l-flex--distribute-space l-flex--end l-flex--wrap o-SearchProductListItem__content__buy-section">
              <div className="u-margin--right o-SearchProductListItem__content__buy-section-wrapper">
                {!isRebrandingEnabled && renderProductItemLinkWithIcon}
                <div className="l-flex o-SearchProductListItem__prices__retail-price-wrapper">
                  {discountBadge}
                  {retailPriceComponent}
                </div>
                {unitPricingComponent}
                {listPriceWithDiscount}
              </div>
              <div className="l-flex@medium l-flex--justify-content--end o-SearchProductListItem__content__buy-section__cart-section">
                {addToCartButtonComponent}
              </div>
              {sellerBoxComponent}
            </div>
          </div>
        </div>
      </div>
      {renderProductVariantsBox}
    </li>
  )
}

export default SearchProductListItemComponent
